import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useTutorAnalytics } from '../../tutor/hooks/useTutorAnalytics';
import { Clock, Activity, Mic, TrendingUp, Calendar, MessageSquare, Sparkles, AlertTriangle, RefreshCw } from 'lucide-react';
import { resolveSpeakingTopicLabel } from '../../tutor/types/tutorAnalytics';

interface TutorAnalyticsDashboardProps {
  days?: number;
}

export const TutorAnalyticsDashboard: React.FC<TutorAnalyticsDashboardProps> = ({
  days = 30
}) => {
  const { data, loading, error, refetch } = useTutorAnalytics({ initialDays: days });

  // Show a warning if there are persistent API issues
  const showApiWarning = error && (error.includes('Server error') || error.includes('Cannot connect'));

  const formatDuration = (minutes: number) => {
    const value = Math.round(minutes * 10) / 10;
    return `${value}m`;
  };

  const formatAvgSessionLength = (seconds: number) => {
    if (!seconds || Number.isNaN(seconds)) return '—';
    if (seconds < 60) return `${Math.round(seconds)} sec`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    if (s === 0) return `${m} min`;
    return `${m} min ${s} sec`;
  };

  const formatLastActive = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-600 dark:text-pink-400" />
          Your Tamil Tutor activity
        </h2>
        <p className="text-sm text-app-text-secondary dark:text-gray-400 -mt-4">
          Loading your practice time and visits from the last {days} days…
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-600 dark:text-pink-400" />
          Your Tamil Tutor activity
        </h2>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-4">
              <p className="text-gray-600 dark:text-gray-400">
                {error || 'No analytics data available'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /** Raw session rows in this period (matches the breakdown card below). */
  const tutorVisitsCount =
    data.session_history.length > 0 ? data.session_history.length : data.sessions_count || 0;

  return (
    <div className="mt-8 space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-600 dark:text-pink-400" />
            Your Tamil Tutor activity
          </h2>
          <p className="text-sm text-app-text-secondary dark:text-gray-400 mt-1 max-w-2xl">
            These numbers come from your <strong className="font-medium text-app-text dark:text-gray-200">Tamil Tutor</strong>{' '}
            (voice or text): each time you open a tutor session we save a visit, add up practice minutes, and (for voice)
            score your speaking. Showing the last {days} days.
          </p>
        </div>
        {showApiWarning && (
          <button
            type="button"
            onClick={refetch}
            className="text-sm text-purple-600 hover:text-purple-800 dark:text-pink-400 dark:hover:text-pink-300 flex items-center gap-1 shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        )}
      </div>

      {showApiWarning && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">
                Analytics service temporarily unavailable. Showing cached data where available.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Minutes */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Practice time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-text dark:text-white">
              {data.total_tutor_minutes > 0 ? formatDuration(data.total_tutor_minutes) : '—'}
            </div>
            <p className="text-xs text-app-text-secondary dark:text-gray-500 mt-2 leading-snug">
              Minutes added up from your daily Tamil Tutor use (voice + text).
            </p>
          </CardContent>
        </Card>

        {/* Tutor visits — aligned with session list when present */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Tutor visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-text dark:text-white">
              {tutorVisitsCount > 0 ? tutorVisitsCount : '—'}
            </div>
            <p className="text-xs text-app-text-secondary dark:text-gray-500 mt-2 leading-snug">
              How many times you started a Tamil Tutor session in this period (same list as below).
            </p>
          </CardContent>
        </Card>

        {/* Last Active */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Last practice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold text-app-text dark:text-white">
              {formatLastActive(data.last_active_at)}
            </div>
            <p className="text-xs text-app-text-secondary dark:text-gray-500 mt-2 leading-snug">
              From your daily activity record.
            </p>
          </CardContent>
        </Card>

        {/* Speaking Level */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-secondary dark:text-gray-400 flex items-center gap-2">
              <Mic className="w-4 h-4" />
              Speaking level
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-text dark:text-white">
              {data.latest_speaking_level || '—'}
            </div>
            <p className="text-xs text-app-text-secondary dark:text-gray-500 mt-2 leading-snug">
              From your most recent voice practice feedback (e.g. A1, A2).
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Visit breakdown (same sessions as “Tutor visits”) */}
      {data.session_history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                How you practiced
              </span>
              <span className="text-sm font-normal text-app-text-secondary dark:text-gray-400">
                {data.session_history.length} visit{data.session_history.length === 1 ? '' : 's'} in the last {days} days
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-pink-500 bg-clip-text text-transparent dark:from-indigo-400 dark:to-pink-400">
                  {data.session_history.filter(s => s.mode === 'VOICE').length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">With voice</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {data.session_history.filter(s => s.mode === 'TEXT').length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">With text chat</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {formatAvgSessionLength(
                    data.session_history.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) /
                      data.session_history.length
                  )}
                </div>
                <div className="text-gray-600 dark:text-gray-400">Typical visit length</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent voice feedback */}
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-1">
              <span className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Recent voice feedback
              </span>
              <span className="text-sm font-normal text-app-text-secondary dark:text-gray-400">
                Scores from your last voice practices in this period
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.speaking_sessions.length === 0 ? (
              <p className="text-gray-500 text-sm">No voice scores yet — try a voice lesson in Tamil Tutor.</p>
            ) : (
              <div className="space-y-3">
                {data.speaking_sessions.slice(0, 2).map((session) => (
                  <div key={session.session_id} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-app-text dark:text-white">
                          Voice practice
                        </span>
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/60 text-purple-800 dark:text-purple-200 px-2 py-1 rounded border border-purple-200/80 dark:border-purple-700/60">
                          {session.speaking_level}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatDuration(session.duration_seconds / 60)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      <span className="text-gray-500">Topic: </span>
                      <span className="font-medium text-app-text dark:text-gray-200">
                        {resolveSpeakingTopicLabel(session, data.session_history) || '—'}
                      </span>
                    </p>
                    <div className="space-y-3">
                      {[
                        { label: 'Pronunciation', value: session.scores.pronunciation, color: 'bg-emerald-500' },
                        { label: 'Fluency', value: session.scores.fluency, color: 'bg-gradient-to-r from-indigo-500 to-purple-500' },
                        { label: 'Grammar', value: session.scores.grammar, color: 'bg-violet-500' },
                        { label: 'Overall', value: session.scores.overall, color: 'bg-indigo-500', bold: true },
                      ].map(({ label, value, color, bold }) => (
                        <div key={label} className="flex items-center gap-2">
                          <span className={`text-xs text-gray-500 dark:text-gray-400 w-24 shrink-0 ${bold ? 'font-semibold text-gray-700 dark:text-gray-300' : ''}`}>
                            {label}:
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${color} transition-all duration-500`}
                              style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium w-8 text-right ${bold ? 'font-bold text-app-text dark:text-white' : ''}`}>
                            {value}%
                          </span>
                        </div>
                      ))}
                    </div>
                    {/* {session.feedback_ta && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                        {session.feedback_ta}
                      </p>
                    )} */}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Practice by day */}
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-1">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Practice by day
              </span>
              <span className="text-sm font-normal text-app-text-secondary dark:text-gray-400">
                Visits and minutes saved per calendar day
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_engagement.length === 0 ? (
              <p className="text-gray-500 text-sm">No day-by-day summary yet — it fills in as you use the tutor.</p>
            ) : (
              <div className="space-y-3">
                {data.daily_engagement.slice(0, 5).map((day) => (
                  <div key={day.date} className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium text-app-text dark:text-white">
                        {new Date(day.date).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {day.sessions} visit{day.sessions === 1 ? '' : 's'} that day
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-app-text dark:text-white">
                        {formatDuration(day.tutor_minutes)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        that day
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};